/**
 * Central Subscription & Entitlement Access Engine.
 * Replaces legacy licence-key verification for runtime access decisions.
 *
 * Flow: Auth → Membership → Active Tenant → Subscription → Plan → Entitlements → Access
 */
import { create } from "zustand";
import { dataProvider as supabase } from "@/lib/providers/data-provider";
import { DEFAULT_AVS_PRODUCT, type AvsProductId } from "@/lib/comm/platform/communication-events";
import { fetchMyMemberships } from "@/lib/identity/membership-service";
import { DEFAULT_PUBLIC_TRIAL_DAYS } from "@/lib/auth/public-signup-policy";

export type SubscriptionAccessStatus =
  | "checking"
  | "TRIAL_ACTIVE"
  | "TRIAL_EXPIRING"
  | "TRIAL_EXPIRED"
  | "ACTIVE"
  | "GRACE_PERIOD"
  | "PAST_DUE"
  | "PAYMENT_PENDING"
  | "EXPIRED"
  | "SUSPENDED"
  | "CANCELLED"
  | "NO_SUBSCRIPTION"
  | "NO_MEMBERSHIP"
  | "NO_TENANT"
  | "PLATFORM_OWNER";

export type SubscriptionAccessLevel =
  "granted" | "granted_limited" | "billing_only" | "subscription_required" | "denied";

export interface SubscriptionAccessSnapshot {
  access: SubscriptionAccessLevel;
  status: SubscriptionAccessStatus;
  valid: boolean;
  message: string | null;
  organizationId: string | null;
  productId: string;
  trialEndsAt: number | null;
  expiry: number | null;
  daysRemaining: number | null;
  edition: string | null;
  planCode: string | null;
  planName: string | null;
  features: Record<string, boolean>;
  limits: Record<string, unknown>;
  lastCheckedAt: number | null;
}

interface SubscriptionAccessState extends SubscriptionAccessSnapshot {
  checking: boolean;
  refresh: (organizationId?: string | null) => Promise<SubscriptionAccessSnapshot>;
  reset: () => void;
}

const INITIAL: SubscriptionAccessSnapshot = {
  access: "denied",
  status: "checking",
  valid: false,
  message: null,
  organizationId: null,
  productId: DEFAULT_AVS_PRODUCT,
  trialEndsAt: null,
  expiry: null,
  daysRemaining: null,
  edition: null,
  planCode: null,
  planName: null,
  features: {},
  limits: {},
  lastCheckedAt: null,
};

function snapshotFromMembership(
  organizationId: string,
  subscriptionStatus: string | null | undefined,
): SubscriptionAccessSnapshot | null {
  const status = String(subscriptionStatus ?? "").toLowerCase();
  if (!status) return null;
  if (status === "active") {
    return {
      ...INITIAL,
      access: "granted",
      status: "ACTIVE",
      valid: true,
      message: "Subscription active.",
      organizationId,
      lastCheckedAt: Date.now(),
    };
  }
  if (status === "trial") {
    return {
      ...INITIAL,
      access: "granted",
      status: "TRIAL_ACTIVE",
      valid: true,
      message: "Trial active.",
      organizationId,
      lastCheckedAt: Date.now(),
    };
  }
  if (status === "pending_card") {
    return {
      ...INITIAL,
      access: "billing_only",
      status: "PAYMENT_PENDING",
      valid: false,
      message: "Add a valid card to start your free trial.",
      organizationId,
      lastCheckedAt: Date.now(),
    };
  }
  return null;
}

async function membershipSubscriptionFallback(
  organizationId: string | null,
): Promise<SubscriptionAccessSnapshot | null> {
  const memberships = await fetchMyMemberships({ productId: DEFAULT_AVS_PRODUCT });
  const match = organizationId
    ? memberships.find((m) => m.organization_id === organizationId)
    : memberships.find((m) => m.is_active) ?? memberships[0];
  if (match) {
    const snap = snapshotFromMembership(match.organization_id, match.subscription_status);
    if (snap) return snap;
  }

  // Local trial fallback (signup before webhook confirms)
  if (typeof window !== "undefined") {
    const localTrialEnd = localStorage.getItem("ornexa_trial_ends_at");
    if (localTrialEnd) {
      const endsAt = new Date(localTrialEnd).getTime();
      const now = Date.now();
      if (endsAt > now) {
        const remainingDays = Math.max(1, Math.ceil((endsAt - now) / 86400000));
        return {
          ...INITIAL,
          access: "granted",
          status: remainingDays <= 1 ? "TRIAL_EXPIRING" : "TRIAL_ACTIVE",
          valid: true,
          message: `${DEFAULT_PUBLIC_TRIAL_DAYS}-day free trial active.`,
          organizationId: organizationId || "trial_org",
          trialEndsAt: endsAt,
          daysRemaining: remainingDays,
          lastCheckedAt: Date.now(),
        };
      } else {
        return {
          ...INITIAL,
          access: "subscription_required",
          status: "TRIAL_EXPIRED",
          valid: false,
          message: `Your ${DEFAULT_PUBLIC_TRIAL_DAYS}-day free trial has expired. Restart trial or choose a plan.`,
          organizationId: organizationId || "trial_org",
          daysRemaining: 0,
          lastCheckedAt: Date.now(),
        };
      }
    }
  }

  return null;
}

function mapPayload(raw: Record<string, unknown>): SubscriptionAccessSnapshot {
  return {
    access: (raw.access as SubscriptionAccessLevel) ?? "denied",
    status: (raw.status as SubscriptionAccessStatus) ?? "EXPIRED",
    valid: Boolean(raw.valid),
    message: (raw.message as string) ?? null,
    organizationId: (raw.organization_id as string) ?? null,
    productId: (raw.product_id as string) ?? DEFAULT_AVS_PRODUCT,
    trialEndsAt: raw.trialEndsAt != null ? Number(raw.trialEndsAt) : null,
    expiry: raw.expiry != null ? Number(raw.expiry) : null,
    daysRemaining: raw.daysRemaining != null ? Number(raw.daysRemaining) : null,
    edition: (raw.edition as string) ?? null,
    planCode: (raw.planCode as string) ?? null,
    planName: (raw.planName as string) ?? null,
    features: (raw.features as Record<string, boolean>) ?? {},
    limits: (raw.limits as Record<string, unknown>) ?? {},
    lastCheckedAt: Date.now(),
  };
}

export const useSubscriptionAccess = create<SubscriptionAccessState>()((set, get) => ({
  ...INITIAL,
  checking: true,
  async refresh(organizationId) {
    set({ checking: true, status: "checking" });
    try {
      const { data, error } = await (supabase as any).rpc("resolve_subscription_access", {
        p_organization_id: organizationId ?? null,
        p_product_id: DEFAULT_AVS_PRODUCT,
      });
      if (error) throw error;
      const snapshot = mapPayload((data ?? {}) as Record<string, unknown>);
      if (!isSubscriptionAccessGranted(snapshot)) {
        const fromMembership = await membershipSubscriptionFallback(organizationId ?? null);
        if (fromMembership && isSubscriptionAccessGranted(fromMembership)) {
          set({ ...fromMembership, checking: false });
          return fromMembership;
        }
      }
      set({ ...snapshot, checking: false });
      return snapshot;
    } catch (err) {
      const fromMembership = await membershipSubscriptionFallback(organizationId ?? null);
      if (fromMembership && isSubscriptionAccessGranted(fromMembership)) {
        set({ ...fromMembership, checking: false });
        return fromMembership;
      }
      const message = err instanceof Error ? err.message : "Subscription check failed";
      const fallback: SubscriptionAccessSnapshot = {
        ...INITIAL,
        status: "EXPIRED",
        message,
        lastCheckedAt: Date.now(),
      };
      set({ ...fallback, checking: false });
      return fallback;
    }
  },
  reset() {
    set({ ...INITIAL, checking: true });
  },
}));

export async function resolveSubscriptionAccess(opts?: {
  organizationId?: string | null;
  productId?: AvsProductId;
}): Promise<SubscriptionAccessSnapshot> {
  return useSubscriptionAccess.getState().refresh(opts?.organizationId ?? null);
}

export function isSubscriptionAccessGranted(snapshot: SubscriptionAccessSnapshot): boolean {
  return (
    snapshot.valid ||
    snapshot.access === "granted" ||
    snapshot.access === "granted_limited" ||
    snapshot.status === "PLATFORM_OWNER" ||
    String(snapshot.status).toLowerCase() === "platform_owner"
  );
}
